import { get, isEmpty, isNil, isNull, isObject, join, map } from 'lodash';
import { Response } from 'express';
import React from 'react';
import dynamic from 'next/dynamic';
import { useSelector } from 'react-redux';
import { PP } from '../../common/analytics/sourceTypes';
import config from '../../common/config';
import { HTTP_RESPONSE_CODES } from '../../common/helpers/constants';
import LocationHelper from '../../common/helpers/domain/LocationHelper';
import UrlHelper from '../../common/helpers/routing/UrlHelper';
import { handleImageUrl } from '../../common/helpers/sharpie/imageUrlHelper';
import { isDesktop, isDevice } from '../../common/helpers/utility/screenHelper';
import HeroImage from '../../components/heroImage/HeroImage';
import { LeadsCapture } from '../../components/leadsCapture/LeadsCapture';
import {
  AsideContainer,
  DisclaimerContainer,
  Divider,
  InnerPageWrapper,
  LeftContainer,
  LeftContainerWrapper,
  ProjectPageLeadsCaptureWrapper,
  ProjectPageWrapper,
} from '../../components/pages/projectPage/ProjectPage.style';
import OtherNearbyProject from '../../components/projectPage/otherNearbyProject/OtherNearbyProject';
import ProjectArticles from '../../components/projectPage/projectArticles/ProjectArticles';
import ProjectBreadcrumb from '../../components/projectPage/projectBreadcrumb/ProjectBreadcrumb';
import ProjectDetail from '../../components/projectPage/projectDetail/ProjectDetail';
import ProjectInformation from '../../components/projectPage/projectInformation/ProjectInformation';
import ProjectOtherContent from '../../components/projectPage/projectOtherContent/ProjectOtherContent';
import ProjectSchema from '../../components/projectPage/projectSchema/ProjectSchema';
import ProjectSummary from '../../components/projectPage/projectSummary/ProjectSummary';
import PropertyOption from '../../components/projectPage/propertyOption/PropertyOption';
import SeoHeader from '../../components/seoHeader/SeoHeader';
import Tags from '../../components/tags/Tags';
import Page from '../../layouts/main';
import projectResource from '../../locales/th/project.json';
import {
  fetchNearbyProjects,
  fetchProjectArticles,
  fetchProjectDetails,
} from '../../services/project';
import { updateCurrentProject } from '../../store/project/slice';
import { updateCurrentSourcePage } from '../../store/search/slice';
import Error from '../_error';
import { Disclaimer } from '../../components/disclaimer/Disclaimer';
import { trackProjectSubscribeButton } from '../../common/analytics/analyticsTracker';
import { AdUnits } from '../../common/helpers/ads/dfp';
import { getAdSubsectByPropertyType } from '../../common/helpers/ads/AdsHelper';
import { getPropertyTypeFromUrlSegment } from '../../common/helpers/domain/propertyTypes';
import { WholeState } from '../../store/common/type';
import { NextPage, NextPageContext } from 'next';
import { Store } from 'redux';
import { ParsedUrlQuery } from 'querystring';
import { ApolloClient } from '@apollo/client';
import { TransformedProjectNetDataType } from '../../common/helpers/data/transformProjectnetProjectData';
import { ProjectArticleData } from '../../queries/projectPage/projectArticles';

// Importing ads components dynamically to avoid loading them in the server side rendering
const Billboard = dynamic(() => import('../../components/ads/Billboard'), {
  ssr: false,
});
const CardsAds = dynamic(() => import('../../components/ads/CardsAds'), {
  ssr: false,
});

const imageWidth = 365;
const imageHeight = 220;

type ProjectData = {
  id: number;
  title: string;
  propertyType: string | string[];
  developer: { name: string } | string;
  location: {
    district: { value: string }[];
    zone: { value: string }[];
  };
  transportations: string[];
  priceTypes: string[];
  status: string;
  link: string;
  featuredImage: {
    height: number;
    title: string;
    url: string;
    width: number;
  };
  heroImage: {
    height: number;
    title: string;
    url: string;
    width: number;
  };
  media: string[];
  isProjectnetProject: boolean;
};
interface ProjectPageQuery extends ParsedUrlQuery {
  params?: string;
}
type ProjectPageProps = {
  canonicalLink: string;
  data: {
    loading: boolean;
    project:
      | (ProjectData & { isProjectnetProject: boolean })
      | TransformedProjectNetDataType
      | null;
    error?: string;
  };
  window?: Window;
  nearbyProjectData: NearbyProjectData;
  projectArticleData: ProjectArticleData;
  asPath?: string;
  id?: string;
  slug?: string;
};
type ProjectPageContext = NextPageContext & {
  store: Store;
  query: ProjectPageQuery;
  res: Response;
  apolloClient: ApolloClient<unknown>;
};
type NearbyProjectData = object;
const ProjectPage: NextPage<ProjectPageProps> = (props) => {
  const loading = get(props, 'data.loading');
  const project = get(props, 'data.project', null);
  const isProjectnetProject = Boolean(project?.isProjectnetProject);
  const error = get(props, 'data.error');
  const deviceType = useSelector((state: WholeState) =>
    state.common !== undefined ? state.common.deviceType : 'desktop',
  );
  const userInfo = useSelector((state: WholeState) =>
    state !== undefined ? state.common.user : {},
  );

  if (!loading && isEmpty(project)) {
    return <Error statusCode={HTTP_RESPONSE_CODES.NotFound} />;
  }
  if (!isEmpty(error)) {
    return <Error statusCode={HTTP_RESPONSE_CODES.ServerError} />;
  }

  const formatProjectMeta = () => {
    const metaDescriptionText1 = projectResource.metaDescriptionText.text1;
    const metaDescriptionText2 = projectResource.metaDescriptionText.text2;
    const metaDescriptionText3 = projectResource.metaDescriptionText.text3;

    if (project.id) {
      const { propertyType = [], location = {}, title } = project;
      const { zone = [], district = [] } = location;
      let newLocation = '';
      if (!isEmpty(district) && district.length > 0) {
        newLocation = LocationHelper.getDistrict(district);
      } else if (!isEmpty(zone) && zone.length > 0) {
        newLocation = LocationHelper.getZone(zone);
      }
      let metaTitle = title;
      metaTitle += !isEmpty(propertyType)
        ? ` [${join(propertyType, ', ')}]`
        : '';
      metaTitle += !isNull(newLocation) ? `- ${newLocation}` : '';
      metaTitle += ' | ThinkOfLiving.Com';

      let metaDescription = `${metaDescriptionText1} ${title}`;
      metaDescription += !isEmpty(propertyType)
        ? ` [${join(propertyType, ', ')}]`
        : '';
      metaDescription += metaDescriptionText2;
      metaDescription += !isNull(LocationHelper.getZone(zone))
        ? ` ${LocationHelper.getZone(zone)}`
        : '';
      metaDescription += !isNull(LocationHelper.getDistrict(district))
        ? `,${LocationHelper.getDistrict(district)}`
        : '';
      metaDescription += ` ${metaDescriptionText3}`;

      return {
        title: metaTitle,
        description: metaDescription,
      };
    } else {
      return {
        title: '',
        description: '',
      };
    }
  };

  const { canonicalLink, data } = props;
  const { title, description } = formatProjectMeta();
  const robots = `${config.metaRobotProject}`;
  const { featuredImage = { url: '' } } = (data.project as ProjectData) || {
    featuredImage: { url: '' },
  };
  const sharpieImgUri = handleImageUrl(
    featuredImage.url,
    imageWidth,
    imageHeight,
  );
  const { heroImage = {}, media } = project;
  const seoData = {
    seoTitle: title,
    metaRobot: robots,
    description,
    canonicalLink,
    sharpieImgUri,
  };

  const renderCardAds = () => {
    if (isDevice(deviceType)) {
      return (
        <CardsAds
          project={project}
          adUnit={AdUnits.PP_Billboard1}
          sect={getPropertyTypesSect(project)}
        />
      );
    }
  };

  const renderBillboard = () => {
    if (isDesktop(deviceType)) {
      return (
        <Billboard
          isOnProjectPage
          adUnit={AdUnits.PP_Billboard1}
          sect={getPropertyTypesSect(project)}
          project={project}
        />
      );
    }
  };

  const renderProjectArticles = () => {
    const { title, id } = project;
    const { projectArticleData } = props;

    if (!projectArticleData || !projectArticleData.projectArticles) {
      return null;
    }

    const { projectArticles } = projectArticleData;
    if (!isNull(props.id)) {
      return (
        <ProjectArticles
          id={id}
          projectName={title}
          projectData={project}
          projectArticles={projectArticles}
          sect={getPropertyTypesSect(project)}
        />
      );
    }
  };

  const shouldShowLeadCaptureForm = () => {
    const leadCaptureFormStartDateString = project.leadCaptureFormStartDate;
    const leadCaptureFormEndDateString = project.leadCaptureFormEndDate;
    const leadCaptureFormId = project?.leadCaptureFormId;

    let leadCaptureFormStartDate;
    let leadCaptureFormEndDate;
    const currentDate = new Date();

    if (leadCaptureFormStartDateString) {
      const leadCaptureFormStartDateParts =
        leadCaptureFormStartDateString.split('/');
      leadCaptureFormStartDate = new Date(
        +leadCaptureFormStartDateParts[2],
        leadCaptureFormStartDateParts[1] - 1,
        +leadCaptureFormStartDateParts[0],
      );
    }

    if (leadCaptureFormEndDateString) {
      const leadCaptureFormEndDateParts =
        leadCaptureFormEndDateString.split('/');
      leadCaptureFormEndDate = new Date(
        +leadCaptureFormEndDateParts[2],
        leadCaptureFormEndDateParts[1] - 1,
        +leadCaptureFormEndDateParts[0],
      );
      leadCaptureFormEndDate.setDate(leadCaptureFormEndDate.getDate() + 1);
    }

    if (
      leadCaptureFormId &&
      !leadCaptureFormStartDate &&
      !leadCaptureFormEndDate
    ) {
      return true;
    }

    if (
      leadCaptureFormId &&
      leadCaptureFormStartDate &&
      !leadCaptureFormEndDate
    ) {
      return currentDate >= leadCaptureFormStartDate;
    }

    if (
      leadCaptureFormId &&
      !leadCaptureFormStartDate &&
      leadCaptureFormEndDate
    ) {
      return currentDate < leadCaptureFormEndDate;
    }

    if (
      leadCaptureFormId &&
      leadCaptureFormStartDate &&
      leadCaptureFormEndDate
    ) {
      return (
        currentDate >= leadCaptureFormStartDate &&
        currentDate < leadCaptureFormEndDate
      );
    }

    return false;
  };

  const renderMainContent = (isProjectnetProject = false) => {
    const { window } = props;
    const { tags = [], units = [] } = project;

    return (
      <LeftContainer>
        <ProjectSummary projectSummaryData={project} />
        <ProjectInformation projectInformationData={project} />
        {!loading && renderProjectArticles()}
        <ProjectDetail window={window} projectData={project} />
        <PropertyOption
          projectData={units}
          isProjectnetProject={isProjectnetProject}
        />
        {shouldShowLeadCaptureForm() && (
          <ProjectPageLeadsCaptureWrapper>
            <LeadsCapture
              leadCaptureFormId={project.leadCaptureFormId}
              projectId={project.id}
              onClickRegisterButton={() =>
                trackProjectSubscribeButton(userInfo, project, PP.PAGE)
              }
            />
          </ProjectPageLeadsCaptureWrapper>
        )}
        <Tags projectDataTags={tags} isOnProjectPage />
        {!loading && renderBillboard()}
        {!loading && renderCardAds()}
      </LeftContainer>
    );
  };

  const renderOtherNearbyProject = () => {
    const district = get(project, 'location.district', []);
    const { nearbyProjectData } = props;

    if (!loading && !isNull(district)) {
      return <OtherNearbyProject nearbyProjectData={nearbyProjectData} />;
    }
  };

  const getPropertyTypesSect = (project: { propertyType?: string[] }) => {
    const propertyTypesSect: string[] = [];

    project?.propertyType?.map((propertyType: string) => {
      propertyTypesSect.push(
        getAdSubsectByPropertyType(getPropertyTypeFromUrlSegment(propertyType)),
      );
    });

    return propertyTypesSect;
  };

  return (
    <Page source={PP.PAGE} shouldCollapseEmptyAdSlots>
      <ProjectSchema project={project} seoData={seoData} />
      <SeoHeader
        title={title}
        description={description}
        canonicalLink={canonicalLink}
        imageUrl={sharpieImgUri}
      />
      <ProjectPageWrapper>
        <ProjectBreadcrumb
          project={project}
          isProjectnetProject={isProjectnetProject}
        />
        <HeroImage
          heroImageData={heroImage}
          isOnProjectPage={true}
          media={media}
        />
        <InnerPageWrapper>
          <LeftContainerWrapper data-test={'project-left-container'}>
            {renderMainContent(isProjectnetProject)}
          </LeftContainerWrapper>
          <AsideContainer data-test={'project-aside-container'}>
            <ProjectOtherContent
              project={project}
              deviceType={deviceType}
              isOnProjectPage
              loading={loading}
              sect={getPropertyTypesSect(project)}
            />
          </AsideContainer>
        </InnerPageWrapper>
      </ProjectPageWrapper>
      {!loading && renderOtherNearbyProject()}
      <Divider />
      <DisclaimerContainer>
        <Disclaimer />
      </DisclaimerContainer>
    </Page>
  );
};

ProjectPage.getInitialProps = async ({
  store,
  query,
  res,
  asPath,
  apolloClient,
}: ProjectPageContext): Promise<ProjectPageProps> => {
  const { params } = query;
  const canonicalLink = config.baseUrl + asPath.substring(1);

  // Extracting project id
  const urlPath = UrlHelper.parsePath(params);
  const projectId = urlPath.id;

  // Default response in case of invalid project id
  let postData: {
    project: ProjectData;
    loading: boolean;
    error: string;
  } | null = null;
  let nearbyProjectData;
  let projectArticleData;

  if (projectId && isFinite(parseInt(projectId))) {
    const variables = {
      id: parseInt(projectId),
      errorPolicy: 'all',
    };

    try {
      // Fetching project data
      postData = await fetchProjectDetails(apolloClient, variables);

      if (
        Object.prototype.hasOwnProperty.call(postData, 'project') &&
        postData.project !== null
      ) {
        const {
          id,
          title,
          propertyType,
          developer,
          location,
          transportations,
          priceTypes,
          status,
          link,
        } = postData.project;

        if (status === 'private' && !isNil(res)) {
          res.redirect(302, config.baseUrl);
        }

        const projectUrlInfo = UrlHelper.getPostUrlInfo({ link }, asPath, true);
        if (!projectUrlInfo.isValid) {
          res.redirect(
            301,
            projectUrlInfo.actualUrl + UrlHelper.getQueryStringFromUrl(asPath),
          );
        }

        const stringPropertyType = Array.isArray(propertyType)
          ? propertyType.join(',')
          : String(propertyType);
        const developerName = isObject(developer)
          ? developer.name
          : String(developer);

        // Setting project data in store
        store.dispatch(
          updateCurrentProject({
            id,
            title,
            propertyType: stringPropertyType,
            developer: developerName,
            location,
            transportations,
            priceTypes,
          }),
        );
        store.dispatch(updateCurrentSourcePage(PP.PAGE));

        const district = get(location, 'district', []);
        const projectLocation = map(district, 'value');
        const [nearbyProject, projectArticle] = await Promise.all([
          fetchNearbyProjects(apolloClient, {
            projectId: parseInt(projectId),
            locationDistrictId:
              projectLocation.length > 0 ? parseInt(projectLocation[0]) : NaN,
          }),
          fetchProjectArticles(apolloClient, {
            projectId: parseInt(projectId),
          }),
        ]);
        nearbyProjectData = nearbyProject;
        projectArticleData = projectArticle;
      }
    } catch (e) {
      console.log(
        'Error when getting post detail in project page component:',
        e,
      );
    }
  }

  return {
    asPath,
    id: projectId || '',
    slug: urlPath.slug,
    canonicalLink,
    data: postData,
    nearbyProjectData,
    projectArticleData,
    window: globalThis.window,
  };
};

export default ProjectPage;
